<ng-container *ngIf="{
  investorsOverviewModel: investorsOverviewModel$ | async,
  currency: currency$ | async,
  fund: fund$ | async,
  isLoading: isLoading$ | async
 } as data">

  <terra-loader class="page-loader" *ngIf="data.isLoading" />

  <section class="empty-state"
           *ngIf="!data.isLoading && data.fund.status == HoldingStatus.Draft && (!data.investorsOverviewModel || data.investorsOverviewModel.length == 0)">
    <img src="assets/images/general/empty_state.svg" />
    <h2>No contributions yet</h2>
    <a [routerLink]="importContributionRoute$ | async"
       class="import-contribution-button-empty" type="button" mat-flat-button color="primary">
      <mat-icon class="notranslate material-icons-outlined">cloud_upload</mat-icon>
      Import Contribution
    </a>
  </section>

  <section class="overview-section" *ngIf="!data.isLoading && data.investorsOverviewModel && data.investorsOverviewModel.length > 0">

    <p-table #pTable [value]="data.investorsOverviewModel" [columns]="cols$ | async" [showCurrentPageReport]="true" [alwaysShowPaginator]="true"
             [showFirstLastIcon]="false" [tableStyle]="{ 'min-width': '50rem' }" [styleClass]="'p-datatable-table'" [rowHover]="true"
             [globalFilterFields]="['name']" responsiveLayout="scroll" *ngIf="data.investorsOverviewModel"
             [scrollable]="true" scrollHeight="flex" (sortFunction)="tableSort($event)" [customSort]="true"
             [totalRecords]="data.investorsOverviewModel.length" [rowsPerPageOptions]="[10, 25, 50, 100]"
             currentPageReportTemplate="{first} - {last} of {totalRecords}">
      <ng-template pTemplate="caption">
        <div class="flex align-items-center justify-content-between">
          <div class="flex">
                <terra-search-field class="search-field" placeholder="Search investors" [(filter)]="filteringText"
                  (filterChange)="filterChanged($event, 'contains')" (clearSearch)="clearSearch()" />
                @if (promoteToDate()) {
                  <div class="p-2 ml-2">Promote to date: {{promoteToDate()}}</div>
                }
          </div>
          <div class="flex header-actions">
            <terra-buttons class="action-button" type="button" mat-stroked-button type="secondary" *ngIf="commitmentsEnabled$ | async"
                           terraCheckPermissions [minimumPermissionLevel]="PermissionLevel.Editor" [noPermissionAction]="NoPermissionAction.PopupOnClick"
                           (onClick)="sendManageCommitmentTelemetry(false)"
                           [routerLink]="investorsManagementRoute$ | async" label="Manage Commitments">
            </terra-buttons>
            <terra-buttons class="action-button" type="button" mat-stroked-button type="secondary"
                           (onClick)="openOwnershipTransferDialog()" label="Transfer Ownership" icon="cached">
            </terra-buttons>
          </div>
        </div>
      </ng-template>
      <ng-template pTemplate="header" #headerTemplate>
        <tr class="text-xs">
          <th class="w-1rem"></th>
          <th [pSortableColumn]="col.name" [pSortableColumnDisabled]="!col.shouldSort" *ngFor="let col of cols$ | async" [ngClass]="{ 'numeralHeader': col.type != 'string' }">
            {{ col.displayName }}
            <mat-icon *ngIf="col.name === 'ownership'"
                      class="table-tooltip-icon" hideDelay="2000" tooltipPosition="bottom" tooltipStyleClass="text-center wider" [pTooltip]="overviewTooltip$ | async">
              help_outline
            </mat-icon>
            <mat-icon *ngIf="col.displayName === AdjustedXirrColName"
                      class="table-tooltip-icon" hideDelay="2000" tooltipPosition="bottom" tooltipStyleClass="wider" [pTooltip]="adjustedXirrTooltip">
              help_outline
            </mat-icon>
            <p-sortIcon *ngIf="col.shouldSort" [field]="col.name"></p-sortIcon>
          </th>
          <th class="font-semibold w-1rem text-left"></th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-columns="columns" let-rowData>
        <tr>
          <td>
            <span class="icon-container">
              <terra-name-icon [svgIcon]="InvestingEntityType.getIcon(rowData['type'])"></terra-name-icon>
            </span>
          </td>
          <td *ngFor="let col of columns">
            <ng-container *ngIf="(col.name === 'totalCommitments' && !!!rowData[col.name]); else valueCols">
              <terra-buttons class="action-button" type="button" mat-stroked-button type="secondary" (onClick)="sendManageCommitmentTelemetry(true)"
                             terraCheckPermissions [minimumPermissionLevel]="PermissionLevel.Editor" [noPermissionAction]="NoPermissionAction.PopupOnClick"
                             [routerLink]="investorsManagementRoute$ | async" [icon]="'add'" label="Commitment">
              </terra-buttons>
            </ng-container>
            <ng-template #valueCols>
              <i *ngIf="showCommitmentWarning(rowData, col.name)">
                <mat-icon class="notranslate aterial-icons-outlined warning-icon"
                          pTooltip="Commitment amount is lower than the contribution"
                          tooltipStyleClass="text-center wide-15">
                  error
                </mat-icon>
              </i>
              <div [ngSwitch]="col.type" class="text-right" [ngClass]="col.getClass ? col.getClass() : {class : false}">
                <ng-container *ngSwitchCase="'currency'">
                  <ng-container *ngIf="!!rowData[col.name]; else noValue">
                    <div class="text-right">
                      {{ data?.currency?.symbol }}{{ rowData[col.name] | terraCurrency }}
                    </div>
                  </ng-container>
                  <ng-template #noValue>-</ng-template>
                </ng-container>
                <ng-container *ngSwitchCase="'currency-always'">
                  <ng-container>{{ data?.currency?.symbol }}{{ rowData[col.name] | terraCurrency }}</ng-container>
                </ng-container>
                <ng-container *ngSwitchCase="'percent'">
                  <ng-container *ngIf="!!rowData[col.name]; else noValue">
                    {{ rowData[col.name] }}%
                  </ng-container>
                  <ng-template #noValue>-</ng-template>
                </ng-container>
                <ng-container *ngSwitchCase="'percentIrr'">
                  <ng-container *ngIf="!!rowData[col.name]; else adjustedXirrPercentage">
                    {{ rowData[col.name] | number: '1.0-2' }}%
                  </ng-container>
                  <ng-template #adjustedXirrPercentage>
                    <ng-container *ngIf="!!rowData['adjustedXirrPercentage']; else noValue">
                      {{ rowData['adjustedXirrPercentage'] | number: '1.0-2' }}%
                    </ng-container>
                  </ng-template>
                  <ng-template #noValue>-</ng-template>
                </ng-container>

                <ng-container *ngSwitchCase="'distributed'">
                  <div class="text-right">
                    {{ rowData[col.name] }}
                  </div>
                </ng-container>

                <ng-container *ngSwitchCase="'contribution'">
                  <div class="vertical-cell">
                    <ng-container *ngIf="!!rowData[col.name]; else noValue">{{ data?.currency?.symbol }}{{ rowData[col.name] | terraCurrency }}</ng-container>
                    <ng-template #noValue>-</ng-template>
                    <span class="pending-contribution"
                          *ngIf="!!rowData.totalPendingContributions">Pending: {{ data?.currency?.symbol }}{{ rowData.totalPendingContributions | terraCurrency }}</span>
                  </div>
                </ng-container>
                <ng-container *ngSwitchDefault>
                  <div class="text-left">
                    {{ rowData[col.name] }}
                    <!-- {{ col.type !== 'Number' ? rowData[col.name] : (rowData[col.name] | terraNumber) }} -->
                  </div>
                </ng-container>
              </div>
            </ng-template>
          </td>
          <td class="action-size">     
            @if(rowData.contactId)
            {
              <button class="action-size p-2" mat-icon-button
                      [matMenuTriggerFor]="investorMoreOptions" [matMenuTriggerData]="{rowData}"
                      aria-label="Menu">
                <mat-icon class="menu-icon">more_vert</mat-icon>
              </button>
            }
            <div class="action-size"></div>
          </td>
        </tr>
      </ng-template>

      <ng-template pTemplate="footer">
        <tr class="overview-footer">
          <td>Total</td>
          <td></td>
          <td class="text-right">{{ !!((totalAmounts$ | async)?.ownershipPercent) ? (totalAmounts$ | async)?.ownershipPercent + '%' : '-' }}</td>
          <td></td>
          <td class="text-right" *ngIf="showCommitmentsColumns$ | async">{{ data?.currency?.symbol }}{{ (totalAmounts$ | async)?.totalCommitmentAmount || 0 | terraCurrency }}</td>
          <td class="text-right">{{ data?.currency?.symbol }}{{ (totalAmounts$ | async)?.totalContributionAmount || 0 | terraCurrency }}</td>
          <td class="text-right"
              *ngIf="showCommitmentsColumns$ | async">
            {{ data?.currency?.symbol }}{{ Math.abs((totalAmounts$ | async)?.totalCommitmentAmount - (totalAmounts$ | async)?.totalContributionAmount) || 0 | terraCurrency }}
          </td>
          <td class="text-right">{{ (totalAmounts$ | async)?.totalDistributionAmount }}</td>
          <td></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <ng-container *ngIf="data.investorsOverviewModel.length; else noData">
          <tr class="c-empty-state">
            <td class="text-center" colspan="9">
              <img src="/assets/images/plg/no-results-con.svg" alt="no data here yet" />
              <p class="text-center font-medium text-lg main-hint">
                No matching results
              </p>
              <p class="text-center secondary-hint">
                Please adjust your search or filter and try again.
              </p>
            </td>
          </tr>
        </ng-container>
        <ng-template #noData>
          <tr class="c-empty-state">
            <td class="text-center" colspan="9">
              <img src="/assets/images/plg/empty-icon.svg" alt="no data here yet" />
              <p class="text-center font-medium text-lg secondary-hint">
                Nothing here yet
              </p>
            </td>
          </tr>
        </ng-template>
      </ng-template>
    </p-table>
  </section>
</ng-container>   

<mat-menu #investorMoreOptions>
  <ng-template matMenuContent let-investor="rowData">
    @if(investor?.contactId)
    {
      <button mat-menu-item
              terraCheckPermissions
              [requireContactsVisibility]="true"
              [minimumViewAsInvestorPermission]="ViewAsInvestorPermission.Allowed" 
              [noPermissionAction]="NoPermissionAction.PopupOnClick"
              (click)="viewAsInvestor(investor.contactId)">
        <mat-icon svgIcon="investing_visibility" />
        <span>View as Investor</span>
      </button>
      @if(investor.ieId)
      {
        <button mat-menu-item
                terraCheckPermissions
                [requireContactsVisibility]="true"
                [noPermissionAction]="NoPermissionAction.PopupOnClick"
                (click)="displayContact(investor.contactId)">
          <mat-icon svgIcon="investing_contact" />
          <span>View Contact</span>
        </button>
        <button mat-menu-item
                terraCheckPermissions
                [requireContactsVisibility]="true"
                [noPermissionAction]="NoPermissionAction.PopupOnClick"
                (click)="displayInvestingEntity(investor.ieId)">
          <mat-icon svgIcon="investing_entity" />
          <span>View Investing Entity</span>
        </button>
      }
      <button mat-menu-item
              (click)="goToPositions('investingEntityId', investor.ieId)">
        <mat-icon svgIcon="investing_pie" />
        <span>View Positions</span>
      </button>
    }
  </ng-template>
</mat-menu>
